import { type CalendarDate, today, getLocalTimeZone, startOfMonth, endOfMonth } from "@internationalized/date";
import { type FC, useState, useMemo, useCallback } from "react";

import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/flexkit/typography/Text";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogBody,
    DialogFooter,
    DialogClose,
} from "@/components/podkit/dialog/Dialog";
import { RadioGroup, RadioGroupItem } from "@/components/podkit/forms/RadioListField";
import { TrackLocations } from "@/hooks/use-segment";
import { DateRangePickerField } from "@/routes/settings/organization/enterprise-usage/DateRangePickerField";

type DateRangeOption = "current-month" | "last-month" | "last-12-months" | "custom";

type ExportCsvModalProps = {
    open: boolean;
    onClose: () => void;
    onExport: (startDate: Date, endDate: Date) => void;
    isExporting: boolean;
};

function calendarDateToUTCDate(cd: CalendarDate): Date {
    return new Date(Date.UTC(cd.year, cd.month - 1, cd.day));
}

function calendarDateToUTCEndOfDay(cd: CalendarDate): Date {
    return new Date(Date.UTC(cd.year, cd.month - 1, cd.day, 23, 59, 59, 999));
}

function formatMonthRange(start: CalendarDate, end: CalendarDate): string {
    const startDate = calendarDateToUTCDate(start);
    const endDate = calendarDateToUTCDate(end);
    const startStr = startDate.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
    const endStr = endDate.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
    return `${startStr}\u2013${endStr}`;
}

export const ExportCsvModal: FC<ExportCsvModalProps> = ({ open, onClose, onExport, isExporting }) => {
    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent data-track-location={TrackLocations.ExportCsvModal} className="overflow-visible">
                {/*
                 * Rendering the body unconditionally (rather than gating on `open`) lets the body
                 * unmount together with DialogContent after Radix's exit animation completes.
                 * Gating on `open` would collapse the body as soon as closing starts, which looks jarring.
                 * State still resets on each open because DialogContent remounts via Radix Presence.
                 */}
                <ExportCsvModalBody onExport={onExport} isExporting={isExporting} />
            </DialogContent>
        </Dialog>
    );
};

const ExportCsvModalBody: FC<{ onExport: (startDate: Date, endDate: Date) => void; isExporting: boolean }> = ({
    onExport,
    isExporting,
}) => {
    const todayDate = useMemo(() => today(getLocalTimeZone()), []);
    const monthStart = useMemo(() => startOfMonth(todayDate), [todayDate]);
    const lastMonthEnd = useMemo(() => endOfMonth(todayDate.subtract({ months: 1 })), [todayDate]);
    const lastMonthStart = useMemo(() => startOfMonth(lastMonthEnd), [lastMonthEnd]);
    const last12MonthsStart = useMemo(() => startOfMonth(todayDate.subtract({ months: 11 })), [todayDate]);

    const [selectedOption, setSelectedOption] = useState<DateRangeOption>("current-month");
    const [customRange, setCustomRange] = useState<{ start: CalendarDate; end: CalendarDate }>({
        start: monthStart,
        end: todayDate,
    });
    const [isCustomRangeInvalid, setIsCustomRangeInvalid] = useState(false);

    const currentMonthLabel = useMemo(() => formatMonthRange(monthStart, todayDate), [monthStart, todayDate]);
    const lastMonthLabel = useMemo(
        () => formatMonthRange(lastMonthStart, lastMonthEnd),
        [lastMonthStart, lastMonthEnd],
    );
    const last12MonthsLabel = useMemo(
        () => formatMonthRange(last12MonthsStart, todayDate),
        [last12MonthsStart, todayDate],
    );

    const isExportDisabled = isExporting || (selectedOption === "custom" && isCustomRangeInvalid);

    const validationError = useMemo(() => {
        if (selectedOption !== "custom" || !isCustomRangeInvalid) return null;
        if (customRange.start.compare(customRange.end) > 0) {
            return "Start date must be before end date";
        }
        if (customRange.end.compare(todayDate) > 0) {
            return "End date cannot be in the future";
        }
        return "Invalid date range";
    }, [selectedOption, isCustomRangeInvalid, customRange, todayDate]);

    const handleExport = useCallback(() => {
        switch (selectedOption) {
            case "current-month":
                onExport(calendarDateToUTCDate(monthStart), calendarDateToUTCEndOfDay(todayDate));
                break;
            case "last-month":
                onExport(calendarDateToUTCDate(lastMonthStart), calendarDateToUTCEndOfDay(lastMonthEnd));
                break;
            case "last-12-months":
                onExport(calendarDateToUTCDate(last12MonthsStart), calendarDateToUTCEndOfDay(todayDate));
                break;
            case "custom":
                onExport(calendarDateToUTCDate(customRange.start), calendarDateToUTCEndOfDay(customRange.end));
                break;
        }
    }, [selectedOption, monthStart, todayDate, lastMonthStart, lastMonthEnd, last12MonthsStart, customRange, onExport]);

    return (
        <>
            <DialogHeader>
                <DialogTitle>Export CSV</DialogTitle>
            </DialogHeader>
            <DialogBody>
                <Text className="mb-3 text-sm font-medium text-content-primary">Date range</Text>
                <RadioGroup
                    value={selectedOption}
                    onValueChange={(v) => {
                        setSelectedOption(v as DateRangeOption);
                    }}
                    className="gap-3"
                >
                    <label className="flex cursor-pointer items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <RadioGroupItem value="current-month" />
                            <Text className="text-sm text-content-primary">Current month</Text>
                        </div>
                        <Text className="text-sm text-content-secondary">{currentMonthLabel}</Text>
                    </label>

                    <label className="flex cursor-pointer items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <RadioGroupItem value="last-month" />
                            <Text className="text-sm text-content-primary">Last month</Text>
                        </div>
                        <Text className="text-sm text-content-secondary">{lastMonthLabel}</Text>
                    </label>

                    <label className="flex cursor-pointer items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <RadioGroupItem value="last-12-months" />
                            <Text className="text-sm text-content-primary">Last 12 months</Text>
                        </div>
                        <Text className="text-sm text-content-secondary">{last12MonthsLabel}</Text>
                    </label>

                    <div className="flex flex-col gap-2">
                        <label className="flex cursor-pointer items-center gap-3">
                            <RadioGroupItem value="custom" />
                            <Text className="text-sm text-content-primary">Custom</Text>
                        </label>
                        {selectedOption === "custom" && (
                            <div className="ml-7">
                                <DateRangePickerField
                                    value={customRange}
                                    onChange={setCustomRange}
                                    maxValue={todayDate}
                                    onValidationChange={setIsCustomRangeInvalid}
                                />
                                {validationError && (
                                    <Text className="mt-1 text-xs text-red-500">{validationError}</Text>
                                )}
                            </div>
                        )}
                    </div>
                </RadioGroup>
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="secondary" disabled={isExporting}>
                        Cancel
                    </Button>
                </DialogClose>
                <Button
                    onClick={handleExport}
                    loading={isExporting}
                    disabled={isExportDisabled}
                    data-tracking-id="export-csv-confirm"
                >
                    Export
                </Button>
            </DialogFooter>
        </>
    );
};
